import type { JobStatusResponse } from "@/types/JobStatusResponse";

export interface StemsZipRef {
  blob_container: string;
  filename: string;
}

export function getJobMetadata(
  jobResult: JobStatusResponse,
): Record<string, unknown> {
  return (
    jobResult.jobMetaData ??
    (jobResult as JobStatusResponse & {
      JobMetaData?: JobStatusResponse["jobMetaData"];
    }).JobMetaData ??
    {}
  );
}

/** URLs/paths from Job Manager status 30 (`DocOperationResultUrls`). */
export function getDocOperationResultUrls(
  jobResult: JobStatusResponse,
): string[] {
  const metadata = getJobMetadata(jobResult);
  const urls =
    metadata.DocOperationResultUrls ?? metadata.docOperationResultUrls;
  const urlList = Array.isArray(urls)
    ? urls.map(String)
    : urls
      ? [String(urls)]
      : [];
  const blobs =
    metadata.DocOperationResultBlobs ?? metadata.docOperationResultBlobs ?? [];
  const blobList = Array.isArray(blobs)
    ? blobs.map(String)
    : blobs
      ? [String(blobs)]
      : [];

  return [...urlList, ...blobList].filter(Boolean);
}

export function findDocOperationResultUrl(
  jobResult: JobStatusResponse,
  matches: (url: string) => boolean,
): string | undefined {
  return getDocOperationResultUrls(jobResult).find(matches);
}

export function requireDocOperationResultUrl(
  jobResult: JobStatusResponse,
  matches: (url: string) => boolean,
  label: string,
): string {
  const url = findDocOperationResultUrl(jobResult, matches);
  if (!url) {
    throw new Error(`${label} did not return DocOperationResultUrls.`);
  }
  return url;
}

export function replaceBlobPathFilename(
  pathOrUrl: string,
  newFilename: string,
): string {
  const normalized = pathOrUrl.replace(/\\/g, "/").split("?")[0];
  const lastSlash = normalized.lastIndexOf("/");
  if (lastSlash < 0) {
    return newFilename;
  }
  return `${normalized.slice(0, lastSlash + 1)}${newFilename}`;
}

export interface MusicBlobRef {
  folder: string;
  fileName: string;
}

/** Resolves `{folder}/{fileName}` relative to `pdfsimpliusers/`. */
export function parseMusicBlobRefFromPath(
  pathOrUrl: string,
  fallbackContainer: string,
): MusicBlobRef {
  const parsed = parseStemsZipRefFromResultUrl(pathOrUrl, fallbackContainer);
  return {
    folder: parsed.blob_container,
    fileName: parsed.filename,
  };
}

export function parseStemsZipRefFromResultUrl(
  url: string,
  fallbackContainer: string,
): StemsZipRef {
  const normalized = url.replace(/\\/g, "/").split("?")[0];
  let relative = normalized;

  const pdfsimpliMarker = "/pdfsimpliusers/";
  const markerIndex = relative.indexOf(pdfsimpliMarker);
  if (markerIndex >= 0) {
    relative = relative.slice(markerIndex + pdfsimpliMarker.length);
  } else if (relative.startsWith("pdfsimpliusers/")) {
    relative = relative.slice("pdfsimpliusers/".length);
  } else if (relative.startsWith("http://") || relative.startsWith("https://")) {
    const pathStart = relative.indexOf("/", relative.indexOf("//") + 2);
    relative =
      pathStart >= 0 ? relative.slice(pathStart + 1).split("/").slice(1).join("/") : relative;
  }

  const slash = relative.lastIndexOf("/");
  if (slash < 0) {
    return { blob_container: fallbackContainer, filename: relative };
  }

  return {
    blob_container: relative.slice(0, slash),
    filename: relative.slice(slash + 1),
  };
}

export function isStemArchiveUrl(url: string): boolean {
  return /_stem(s)?\.zip/i.test(url.split("?")[0]);
}

export function isMusicAudioUrl(url: string): boolean {
  return /\.(wav|mp3)$/i.test(url.split("?")[0]);
}
